import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PasswordInput } from '../../components/ui/password-input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Buildings,
  Users,
  ChartLineUp,
  Crown,
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  UserCircle,
  Shield,
  Warning,
  CheckCircle,
  XCircle,
  SignOut,
  CurrencyDollar,
  Clock,
  Gear,
  ArrowsClockwise,
  Export,
  LockKey,
  Eye,
  Trash
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlatformAdminPage() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard stats
  const [stats, setStats] = useState(null);
  
  // Companies
  const [companies, setCompanies] = useState([]);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [companiesPage, setCompaniesPage] = useState(1);
  const [companySearch, setCompanySearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Users
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  
  // User management modal
  const [userModal, setUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // Reset-password dialog state — controls the inline modal that lets a super-
  // admin set a temporary password for any user (e.g. when a user is locked out
  // and the SendGrid email flow is paused).
  const [resetPwModal, setResetPwModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  
  // Subscription modal
  const [subModal, setSubModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Create Company
  const [createCompanyModal, setCreateCompanyModal] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company_name: '', admin_email: '', admin_first_name: '', admin_last_name: '',
    admin_password: '', country: 'AE', currency: 'AED', industry: 'General',
    phone: '', address: '', plan: 'trial',
  });
  const [creating, setCreating] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Check if user is platform admin
  const isPlatformAdmin = user?.email?.toLowerCase() === 'sgkapps.in@gmail.com' || user?.is_platform_admin;

  useEffect(() => {
    if (isPlatformAdmin) {
      fetchStats();
      fetchCompanies();
      fetchUsers();
      fetchAuditLogs();
    }
  }, [isPlatformAdmin]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/platform/admin/stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchCompanies = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      if (companySearch) params.append('search', companySearch);
      if (planFilter) params.append('plan_filter', planFilter);
      if (statusFilter) params.append('status_filter', statusFilter);
      
      const res = await axios.get(`${API}/api/platform/admin/companies?${params}`, { headers });
      setCompanies(res.data.companies || []);
      setCompaniesTotal(res.data.total || 0);
      setCompaniesPage(page);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      if (userSearch) params.append('search', userSearch);
      if (selectedCompanyId) params.append('company_id', selectedCompanyId);
      
      const res = await axios.get(`${API}/api/platform/admin/users?${params}`, { headers });
      setUsers(res.data.users || []);
      setUsersTotal(res.data.total || 0);
      setUsersPage(page);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await axios.get(`${API}/api/platform/admin/audit-logs?limit=20`, { headers });
      setAuditLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    }
  };

  const handleUserAction = async (action) => {
    if (!selectedUser) return;
    
    try {
      const updateData = {};
      
      if (action === 'activate') {
        updateData.is_active = true;
      } else if (action === 'deactivate') {
        updateData.is_active = false;
      } else if (action === 'force_logout') {
        updateData.force_logout = true;
      }
      
      await axios.patch(
        `${API}/api/platform/admin/users/${selectedUser.id}`,
        updateData,
        { headers }
      );
      
      toast.success(`User ${action === 'activate' ? 'activated' : action === 'deactivate' ? 'deactivated' : 'logged out'} successfully`);
      setUserModal(false);
      fetchUsers(usersPage);
      fetchAuditLogs();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  // ============= Admin Password Reset (for locked-out users) ==================
  const generateTempPassword = () => {
    // 12-char temp password — letters + digits + a symbol, easy to read aloud.
    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < 10; i++) out += charset[Math.floor(Math.random() * charset.length)];
    return out + '!7';
  };

  const openResetPassword = () => {
    setTempPassword(generateTempPassword());
    setResetPwModal(true);
  };

  const submitResetPassword = async () => {
    if (!selectedUser?.email || !tempPassword || tempPassword.length < 8) {
      toast.error('Temporary password must be at least 8 characters');
      return;
    }
    try {
      await axios.post(
        `${API}/api/auth/admin-reset-password`,
        { user_email: selectedUser.email, temporary_password: tempPassword },
        { headers },
      );
      toast.success(`Password reset for ${selectedUser.email}. Share the temporary password securely.`);
      // Keep the dialog open so the admin can copy the password explicitly.
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const copyTempPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      toast.success('Temporary password copied to clipboard');
    } catch {
      toast.error('Could not copy — please select & copy manually.');
    }
  };


  const handleUpdateSubscription = async () => {
    if (!selectedCompany || !newPlan) return;
    
    try {
      await axios.post(
        `${API}/api/platform/admin/subscriptions`,
        {
          company_id: selectedCompany.company_id,
          plan: newPlan
        },
        { headers }
      );
      
      toast.success('Subscription updated successfully');
      setSubModal(false);
      fetchCompanies(companiesPage);
      fetchStats();
      fetchAuditLogs();
    } catch (err) {
      toast.error('Failed to update subscription');
    }
  };

  const handleSuspendSubscription = async (suspend) => {
    if (!selectedCompany) return;
    try {
      await axios.patch(
        `${API}/api/platform/admin/subscriptions/${selectedCompany.company_id}`,
        {
          suspended: suspend,
          suspended_reason: suspend ? 'Suspended by Platform Admin' : null,
        },
        { headers }
      );
      toast.success(suspend ? 'Subscription suspended' : 'Subscription reactivated');
      setSubModal(false);
      fetchCompanies(companiesPage);
      fetchStats();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.company_name || !newCompany.admin_email || !newCompany.admin_password || !newCompany.admin_first_name || !newCompany.admin_last_name) {
      toast.error('Please fill all required fields');
      return;
    }
    if (newCompany.admin_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/platform/admin/companies/create`, newCompany, { headers });
      toast.success(`Company "${newCompany.company_name}" created! Admin: ${newCompany.admin_email}`);
      setCreateCompanyModal(false);
      setNewCompany({ company_name: '', admin_email: '', admin_first_name: '', admin_last_name: '', admin_password: '', country: 'AE', currency: 'AED', industry: 'General', phone: '', address: '', plan: 'trial' });
      fetchCompanies(1);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    }
    setCreating(false);
  };

  const handleClearTestData = async () => {
    if (!window.confirm('This will permanently delete ALL test/sample companies and their data. Real companies (created by you) will be preserved.\n\nThis action cannot be undone. Continue?')) return;
    try {
      const res = await axios.delete(`${API}/api/platform/admin/companies/clear-test-data`, { headers });
      toast.success(`${res.data.deleted_companies} test companies removed. Protected: ${res.data.protected_companies?.join(', ')}`);
      fetchCompanies(1);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear test data');
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-600 border-green-200';
      case 'trial': return 'bg-blue-500/10 text-blue-600 border-blue-200';
      case 'grace_period': return 'bg-amber-500/10 text-amber-600 border-amber-200';
      case 'expired': return 'bg-red-500/10 text-red-600 border-red-200';
      case 'suspended': return 'bg-gray-500/10 text-gray-600 border-gray-200';
      default: return 'bg-gray-500/10 text-gray-600 border-gray-200';
    }
  };

  const getPlanBadge = (plan) => {
    const colors = {
      trial: 'bg-blue-100 text-blue-700',
      basic: 'bg-green-100 text-green-700',
      pro: 'bg-purple-100 text-purple-700',
      enterprise: 'bg-amber-100 text-amber-700'
    };
    return colors[plan] || 'bg-gray-100 text-gray-700';
  };

  if (!isPlatformAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              This page is only accessible to the Platform Admin.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <Crown className="text-amber-500" weight="duotone" />
            Platform Admin
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage all users, companies, and subscriptions across BizCore
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setCreateCompanyModal(true)} data-testid="create-company-btn">
            <Buildings className="w-4 h-4 mr-2" />
            Add Company
          </Button>
          <Button variant="outline" size="icon" onClick={() => { fetchStats(); fetchCompanies(); fetchUsers(); fetchAuditLogs(); }}>
            <ArrowsClockwise className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto flex overflow-x-auto">
          <TabsTrigger value="dashboard" className="flex-1 sm:flex-none flex items-center gap-1.5 text-xs sm:text-sm">
            <ChartLineUp className="w-4 h-4 hidden sm:block" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex-1 sm:flex-none flex items-center gap-1.5 text-xs sm:text-sm">
            <Buildings className="w-4 h-4 hidden sm:block" />
            Companies
          </TabsTrigger>
          <TabsTrigger value="users" className="flex-1 sm:flex-none flex items-center gap-1.5 text-xs sm:text-sm">
            <Users className="w-4 h-4 hidden sm:block" />
            Users
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex-1 sm:flex-none flex items-center gap-1.5 text-xs sm:text-sm">
            <Clock className="w-4 h-4 hidden sm:block" />
            Logs
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Companies</p>
                    <p className="text-3xl font-bold">{stats?.total_companies || 0}</p>
                  </div>
                  <Buildings className="w-10 h-10 text-primary/30" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
                    <p className="text-xs text-green-600">{stats?.active_users || 0} active</p>
                  </div>
                  <Users className="w-10 h-10 text-primary/30" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">New Signups (30d)</p>
                    <p className="text-3xl font-bold">{stats?.recent_signups_30d || 0}</p>
                  </div>
                  <ChartLineUp className="w-10 h-10 text-green-500/30" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Est. Monthly Revenue</p>
                    <p className="text-3xl font-bold">${stats?.revenue_estimate?.monthly || 0}</p>
                  </div>
                  <CurrencyDollar className="w-10 h-10 text-amber-500/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subscription Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Subscriptions by Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats?.subscription_by_plan || {}).map(([plan, count]) => (
                    <div key={plan} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={getPlanBadge(plan)}>{plan.toUpperCase()}</Badge>
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Subscriptions by Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats?.subscription_by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <Badge className={getStatusColor(status)}>{status.replace('_', ' ').toUpperCase()}</Badge>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle>All Companies</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Search companies..."
                      value={companySearch}
                      onChange={(e) => setCompanySearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchCompanies(1)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <Select value={planFilter || "all"} onValueChange={(v) => { setPlanFilter(v === "all" ? "" : v); fetchCompanies(1); }}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); fetchCompanies(1); }}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="grace_period">Grace Period</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="destructive" size="sm" onClick={handleClearTestData} data-testid="clear-test-data-btn" className="text-xs">
                    <Trash className="w-3.5 h-3.5 mr-1" />
                    Clear Test Data
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Company</th>
                      <th className="text-left py-3 px-4 font-medium">Owner</th>
                      <th className="text-center py-3 px-4 font-medium">Users</th>
                      <th className="text-center py-3 px-4 font-medium">Plan</th>
                      <th className="text-center py-3 px-4 font-medium">Status</th>
                      <th className="text-right py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => (
                      <tr key={company.company_id} className="border-b hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{company.company_name}</p>
                            <p className="text-xs text-muted-foreground">{company.currency} • {company.country}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm">{company.owner_name}</p>
                            <p className="text-xs text-muted-foreground">{company.owner_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">{company.user_count}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={getPlanBadge(company.subscription?.plan)}>
                            {(company.subscription?.plan || 'trial').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={getStatusColor(company.subscription?.status)}>
                            {(company.subscription?.status || 'trial').replace('_', ' ')}
                          </Badge>
                          {company.subscription?.in_grace_period && (
                            <span className="block text-xs text-amber-600 mt-1">
                              {company.subscription?.grace_days_remaining}d grace left
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedCompany(company);
                              setNewPlan(company.subscription?.plan || 'trial');
                              setSubModal(true);
                            }}
                          >
                            <Gear className="w-4 h-4 mr-1" />
                            Manage
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {companies.length} of {companiesTotal} companies
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={companiesPage === 1}
                    onClick={() => fetchCompanies(companiesPage - 1)}
                  >
                    <CaretLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Page {companiesPage}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={companies.length < 10}
                    onClick={() => fetchCompanies(companiesPage + 1)}
                  >
                    <CaretRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle>All Users</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchUsers(1)}
                      className="pl-9 w-48"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">User</th>
                      <th className="text-left py-3 px-4 font-medium">Company</th>
                      <th className="text-center py-3 px-4 font-medium">Role</th>
                      <th className="text-center py-3 px-4 font-medium">Status</th>
                      <th className="text-center py-3 px-4 font-medium">Plan</th>
                      <th className="text-right py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <UserCircle className="w-8 h-8 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{u.first_name} {u.last_name}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">{u.company_name}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline">{u.role}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {u.is_active ? (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={getPlanBadge(u.subscription_plan)}>
                            {(u.subscription_plan || 'trial').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(u);
                              setUserModal(true);
                            }}
                          >
                            <Gear className="w-4 h-4 mr-1" />
                            Manage
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {users.length} of {usersTotal} users
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={usersPage === 1}
                    onClick={() => fetchUsers(usersPage - 1)}
                  >
                    <CaretLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Page {usersPage}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={users.length < 10}
                    onClick={() => fetchUsers(usersPage + 1)}
                  >
                    <CaretRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="logs" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Platform Audit Logs</CardTitle>
              <CardDescription>Recent administrative actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {auditLogs.map((log) => (
                  <div key={log.id} className="p-3 border rounded-lg bg-muted/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{log.action}</Badge>
                        {log.target_email && (
                          <span className="text-sm">User: {log.target_email}</span>
                        )}
                        {log.company_name && (
                          <span className="text-sm">Company: {log.company_name}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      By: {log.performed_by}
                    </p>
                  </div>
                ))}
                {auditLogs.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No audit logs yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Management Modal */}
      <Dialog open={userModal} onOpenChange={setUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage User</DialogTitle>
            <DialogDescription>
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Company:</span>
                  <p className="font-medium">{selectedUser.company_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Role:</span>
                  <p className="font-medium">{selectedUser.role}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p className="font-medium">{selectedUser.is_active ? 'Active' : 'Inactive'}</p>
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <h4 className="font-medium">Actions</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedUser.is_active ? (
                    <Button variant="destructive" size="sm" onClick={() => handleUserAction('deactivate')}>
                      <XCircle className="w-4 h-4 mr-1" />
                      Deactivate User
                    </Button>
                  ) : (
                    <Button variant="default" size="sm" onClick={() => handleUserAction('activate')}>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Activate User
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleUserAction('force_logout')}>
                    <SignOut className="w-4 h-4 mr-1" />
                    Force Logout
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openResetPassword}
                    data-testid="admin-reset-password-btn"
                    className="border-amber-300 text-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  >
                    <LockKey className="w-4 h-4 mr-1" />
                    Reset Password
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Password Reset Modal — used when a user is locked out and the
          email-based reset flow is unavailable (e.g. SendGrid not configured). */}
      <Dialog open={resetPwModal} onOpenChange={setResetPwModal}>
        <DialogContent className="max-w-md" data-testid="admin-reset-password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LockKey className="w-5 h-5 text-amber-600" />
              Reset Password — {selectedUser?.email}
            </DialogTitle>
            <DialogDescription>
              Set a temporary password for this user. They will be forced to change it on next login.
              Share it through a secure channel (Signal, WhatsApp, in-person) — do NOT paste in chat.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Temporary password
              </label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  className="font-mono text-base"
                  data-testid="admin-reset-password-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTempPassword(generateTempPassword())}
                  data-testid="admin-reset-password-regen"
                  title="Regenerate"
                >
                  <ArrowsClockwise className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyTempPassword}
                  data-testid="admin-reset-password-copy"
                  title="Copy to clipboard"
                >
                  Copy
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                10 alphanumeric chars + <code>!7</code>. Edit freely if you want a memorable one.
              </p>
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-900 dark:text-amber-200">
              <strong>What happens next:</strong>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Failed-login attempts and lockout will be cleared.</li>
                <li>User must change this password on their next login.</li>
                <li>This action is recorded in the platform audit log.</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwModal(false)}>Cancel</Button>
            <Button
              onClick={submitResetPassword}
              data-testid="admin-reset-password-submit"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Subscription Modal */}
      <Dialog open={subModal} onOpenChange={setSubModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Subscription</DialogTitle>
            <DialogDescription>
              {selectedCompany?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Current Plan:</span>
                  <p className="font-medium">
                    <Badge className={getPlanBadge(selectedCompany.subscription?.plan)}>
                      {(selectedCompany.subscription?.plan || 'trial').toUpperCase()}
                    </Badge>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p className="font-medium">
                    <Badge className={getStatusColor(selectedCompany.subscription?.status)}>
                      {selectedCompany.subscription?.status}
                    </Badge>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Users:</span>
                  <p className="font-medium">{selectedCompany.user_count}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Owner:</span>
                  <p className="font-medium">{selectedCompany.owner_email}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <label className="text-sm font-medium">Change Plan</label>
                <Select value={newPlan} onValueChange={setNewPlan}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (30 days)</SelectItem>
                    <SelectItem value="basic">Basic ($29/mo)</SelectItem>
                    <SelectItem value="pro">Pro ($79/mo)</SelectItem>
                    <SelectItem value="enterprise">Enterprise ($199/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSubModal(false)}>Cancel</Button>
            {selectedCompany?.subscription?.suspended ? (
              <Button
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={() => handleSuspendSubscription(false)}
                data-testid="reactivate-sub-btn"
              >
                Reactivate
              </Button>
            ) : (
              <Button
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                onClick={() => handleSuspendSubscription(true)}
                data-testid="suspend-sub-btn"
              >
                Suspend
              </Button>
            )}
            <Button onClick={handleUpdateSubscription} data-testid="update-sub-btn">Update Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Company Modal */}
      <Dialog open={createCompanyModal} onOpenChange={setCreateCompanyModal}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Buildings className="w-5 h-5 text-primary" />
              Add New Company
            </DialogTitle>
            <DialogDescription>Create a company and its Super Admin account</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-4 pr-1">
            <div className="space-y-2">
              <label className="text-sm font-medium">Company Name <span className="text-red-500">*</span></label>
              <Input value={newCompany.company_name} onChange={e => setNewCompany(p => ({...p, company_name: e.target.value}))} placeholder="e.g. ABC Trading LLC" data-testid="new-company-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Country</label>
                <Select value={newCompany.country} onValueChange={v => {
                  const currencies = { AE: 'AED', IN: 'INR', US: 'USD', GB: 'GBP', SA: 'SAR' };
                  setNewCompany(p => ({...p, country: v, currency: currencies[v] || p.currency}));
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AE">UAE</SelectItem>
                    <SelectItem value="IN">India</SelectItem>
                    <SelectItem value="SA">Saudi Arabia</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Currency</label>
                <Input value={newCompany.currency} onChange={e => setNewCompany(p => ({...p, currency: e.target.value}))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone</label>
                <Input value={newCompany.phone} onChange={e => setNewCompany(p => ({...p, phone: e.target.value}))} placeholder="+971 50 000 0000" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Plan</label>
                <Select value={newCompany.plan} onValueChange={v => setNewCompany(p => ({...p, plan: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (30 days)</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-4 mt-2">
              <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                <UserCircle className="w-4 h-4" /> Company Super Admin
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">First Name <span className="text-red-500">*</span></label>
                  <Input value={newCompany.admin_first_name} onChange={e => setNewCompany(p => ({...p, admin_first_name: e.target.value}))} data-testid="new-admin-first-name" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Last Name <span className="text-red-500">*</span></label>
                  <Input value={newCompany.admin_last_name} onChange={e => setNewCompany(p => ({...p, admin_last_name: e.target.value}))} data-testid="new-admin-last-name" />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <label className="text-sm font-medium">Email <span className="text-red-500">*</span></label>
                <Input type="email" value={newCompany.admin_email} onChange={e => setNewCompany(p => ({...p, admin_email: e.target.value}))} placeholder="admin@company.com" data-testid="new-admin-email" />
              </div>
              <div className="space-y-2 mt-3">
                <label className="text-sm font-medium">Password <span className="text-red-500">*</span></label>
                <PasswordInput value={newCompany.admin_password} onChange={e => setNewCompany(p => ({...p, admin_password: e.target.value}))} placeholder="Min 8 characters" data-testid="new-admin-password" />
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 pt-3 border-t">
            <Button variant="outline" onClick={() => setCreateCompanyModal(false)}>Cancel</Button>
            <Button onClick={handleCreateCompany} disabled={creating} data-testid="submit-create-company">
              {creating ? 'Creating...' : 'Create Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
